import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { token } = useAuth();

  const [server, setServer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userPerms, setUserPerms] = useState(0);
  const [userRoles, setUserRoles] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [dms, setDms] = useState([]);
  const [currentDm, setCurrentDm] = useState(null);
  const [view, setView] = useState('server'); // 'server' | 'dms' | 'friends'
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchServer = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server`, { headers: authHeaders() });
      if (res.ok) setServer(await res.json());
    } catch (e) { console.error('Failed to fetch server:', e); }
  }, [token, authHeaders]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server/categories`, { headers: authHeaders() });
      if (res.ok) setCategories(await res.json());
    } catch (e) { console.error('Failed to fetch categories:', e); }
  }, [token, authHeaders]);

  const fetchChannels = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server/channels`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setChannels(data);
        if (!currentChannel && data.length > 0) {
          const textCh = data.find(c => c.channel_type === 'text');
          if (textCh) setCurrentChannel(textCh);
        }
      }
    } catch (e) { console.error('Failed to fetch channels:', e); }
  }, [token, authHeaders, currentChannel]);

  const fetchMembers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server/members`, { headers: authHeaders() });
      if (res.ok) setMembers(await res.json());
    } catch (e) { console.error('Failed to fetch members:', e); }
  }, [token, authHeaders]);

  const fetchRoles = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server/roles`, { headers: authHeaders() });
      if (res.ok) setRoles(await res.json());
    } catch (e) { console.error('Failed to fetch roles:', e); }
  }, [token, authHeaders]);

  const fetchUserPermissions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/server/admin-check`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUserPerms(data.permissions || 0);
        setUserRoles(data.roles || []);
      }
    } catch (e) { console.error('Failed to fetch permissions:', e); }
  }, [token, authHeaders]);

  const fetchFriends = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/friends`, { headers: authHeaders() });
      if (res.ok) setFriends(await res.json());
    } catch (e) { console.error('Failed to fetch friends:', e); }
  }, [token, authHeaders]);

  const fetchFriendRequests = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/friends/requests`, { headers: authHeaders() });
      if (res.ok) setFriendRequests(await res.json());
    } catch (e) { console.error('Failed to fetch friend requests:', e); }
  }, [token, authHeaders]);

  const fetchDMs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/dms`, { headers: authHeaders() });
      if (res.ok) setDms(await res.json());
    } catch (e) { console.error('Failed to fetch DMs:', e); }
  }, [token, authHeaders]);

  const sendFriendRequest = useCallback(async (recipientId) => {
    const res = await fetch(`${API_URL}/api/friends/request`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ recipient_id: recipientId })
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
    await fetchFriendRequests();
  }, [authHeaders, fetchFriendRequests]);

  const acceptFriend = useCallback(async (userId) => {
    await fetch(`${API_URL}/api/friends/${userId}/accept`, { method: 'POST', headers: authHeaders() });
    await Promise.all([fetchFriends(), fetchFriendRequests()]);
  }, [authHeaders, fetchFriends, fetchFriendRequests]);

  const declineFriend = useCallback(async (userId) => {
    await fetch(`${API_URL}/api/friends/${userId}/decline`, { method: 'POST', headers: authHeaders() });
    await fetchFriendRequests();
  }, [authHeaders, fetchFriendRequests]);

  const removeFriend = useCallback(async (userId) => {
    await fetch(`${API_URL}/api/friends/${userId}`, { method: 'DELETE', headers: authHeaders() });
    await fetchFriends();
  }, [authHeaders, fetchFriends]);

  const blockUser = useCallback(async (userId) => {
    await fetch(`${API_URL}/api/friends/${userId}/block`, { method: 'POST', headers: authHeaders() });
    await fetchFriends();
  }, [authHeaders, fetchFriends]);

  const createDM = useCallback(async (recipientId) => {
    const res = await fetch(`${API_URL}/api/dms?recipient_id=${recipientId}`, {
      method: 'POST', headers: authHeaders()
    });
    if (res.ok) {
      const dm = await res.json();
      await fetchDMs();
      return dm;
    }
  }, [authHeaders, fetchDMs]);

  const createChannel = useCallback(async (name, channelType, categoryId, description) => {
    const res = await fetch(`${API_URL}/api/server/channels`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ name, channel_type: channelType, category_id: categoryId, description })
    });
    if (res.ok) {
      await fetchChannels();
      return await res.json();
    }
  }, [authHeaders, fetchChannels]);

  const createCategory = useCallback(async (name, position) => {
    const res = await fetch(`${API_URL}/api/server/categories`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ name, position: position || 0 })
    });
    if (res.ok) {
      await fetchCategories();
      return await res.json();
    }
  }, [authHeaders, fetchCategories]);

  const selectChannel = useCallback((channel) => {
    setCurrentChannel(channel);
    setCurrentDm(null);
    setView('server');
  }, []);

  const selectDm = useCallback((dm) => {
    setCurrentDm(dm);
    setCurrentChannel(null);
    setView('dms');
  }, []);

  const switchToFriends = useCallback(() => {
    setView('friends');
    setCurrentChannel(null);
    setCurrentDm(null);
  }, []);

  const switchToServer = useCallback(() => {
    setView('server');
    setCurrentDm(null);
  }, []);

  useEffect(() => {
    if (token) {
      setLoading(true);
      Promise.all([
        fetchServer(), fetchCategories(), fetchChannels(),
        fetchMembers(), fetchRoles(), fetchUserPermissions(), fetchFriends(),
        fetchFriendRequests(), fetchDMs()
      ]).finally(() => setLoading(false));
    }
  }, [token, fetchServer, fetchCategories, fetchChannels, fetchMembers, fetchRoles, fetchUserPermissions, fetchFriends, fetchFriendRequests, fetchDMs]);

  return (
    <AppContext.Provider value={{
      server, categories, channels, currentChannel, members, roles, userPerms, userRoles,
      friends, friendRequests, dms, currentDm, view, loading,
      fetchServer, fetchCategories, fetchChannels, fetchMembers,
      fetchRoles, fetchUserPermissions, fetchFriends, fetchFriendRequests, fetchDMs,
      sendFriendRequest, acceptFriend, declineFriend, removeFriend, blockUser,
      createDM, createChannel, createCategory,
      selectChannel, selectDm, switchToFriends, switchToServer,
      setChannels, setMembers, setCurrentDm
    }}>
      {children}
    </AppContext.Provider>
  );
};
